/**
 * Theme overrides fetching and caching for web components
 *
 * IMPORTANT: Default theme values come from globals.css which is imported
 * into the Shadow DOM. This module only handles OVERRIDES that a school
 * has explicitly configured.
 *
 * Strategy:
 * - globals.css defines :host { --primary: oklch(...); ... } as defaults
 * - API returns only the overrides a school has set (partial object)
 * - We apply those overrides on top of the CSS defaults
 *
 * Color values can be in any valid CSS format:
 * - hex: #14b8a6
 * - oklch: oklch(0.7787 0.1351 188.7087)
 * - hsl: hsl(174 84% 40%)
 * - rgb: rgb(20 184 166)
 */

import {
  type WebComponentThemeOverrides,
  toKebabCase
} from "@baluu/ui/theme/web-component-defaults";

export type { WebComponentThemeOverrides };

type CachedOverrides = {
  overrides: WebComponentThemeOverrides;
  version: number;
  timestamp: number;
};

const CACHE_KEY_PREFIX = "baluu-theme-";
// Max age before we check version (even with valid cache, periodically verify)
const VERSION_CHECK_INTERVAL_MS = 60 * 1000; // 1 minute

/**
 * Get cache key for a business identifier (slug or id)
 */
function getCacheKey(businessIdentifier: string): string {
  return `${CACHE_KEY_PREFIX}${businessIdentifier}`;
}

/**
 * Check if localStorage is available and accessible
 * (handles sandboxed iframes that throw SecurityError)
 */
function isLocalStorageAvailable(): boolean {
  try {
    const testKey = "__baluu_test__";
    localStorage.setItem(testKey, "1");
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

/**
 * Get cached overrides from localStorage
 */
function getCachedOverrides(businessIdentifier: string): CachedOverrides | null {
  if (!isLocalStorageAvailable()) return null;

  try {
    const cached = localStorage.getItem(getCacheKey(businessIdentifier));
    if (!cached) return null;
    return JSON.parse(cached) as CachedOverrides;
  } catch {
    return null;
  }
}

/**
 * Cache overrides with version in localStorage
 */
function cacheOverrides(
  businessIdentifier: string,
  overrides: WebComponentThemeOverrides,
  version: number
): void {
  if (!isLocalStorageAvailable()) return;

  try {
    const cached: CachedOverrides = {
      overrides,
      version,
      timestamp: Date.now(),
    };
    localStorage.setItem(getCacheKey(businessIdentifier), JSON.stringify(cached));
  } catch {
    // Ignore localStorage errors (quota exceeded, etc.)
  }
}

/**
 * Fetch theme version only (lightweight check)
 */
async function fetchVersionFromAPI(
  businessIdentifier: string,
  apiUrl?: string
): Promise<number> {
  const baseUrl = apiUrl || "https://api.baluu.io";
  const url = `${baseUrl}/widget/theme/${businessIdentifier}/version`;

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch version: ${response.status}`);
  }

  const data = await response.json();
  return data.version as number;
}

/**
 * Fetch theme overrides from API
 * Returns only the values the school has explicitly set
 */
async function fetchOverridesFromAPI(
  businessIdentifier: string,
  apiUrl?: string
): Promise<{ overrides: WebComponentThemeOverrides; version: number }> {
  const baseUrl = apiUrl || "https://api.baluu.io";
  const url = `${baseUrl}/widget/theme/${businessIdentifier}`;

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch theme: ${response.status}`);
  }

  const data = await response.json();

  return {
    overrides: (data.overrides || {}) as WebComponentThemeOverrides,
    version: data.version as number,
  };
}

/**
 * Whether a theme (of any age) is cached locally for this business.
 * Used by the components to decide if the first paint can be branded
 * immediately or the reveal should be briefly held while the theme loads.
 */
export function hasCachedTheme(businessIdentifier: string): boolean {
  return getCachedOverrides(businessIdentifier) !== null;
}

/**
 * Font type configurations
 */
const FONT_CONFIG = {
  sans: { urlKey: "fontSansUrl", nameKey: "fontSansName", cssVar: "--font-sans", fallback: "sans-serif" },
  serif: { urlKey: "fontSerifUrl", nameKey: "fontSerifName", cssVar: "--font-serif", fallback: "serif" },
  mono: { urlKey: "fontMonoUrl", nameKey: "fontMonoName", cssVar: "--font-mono", fallback: "monospace" },
} as const;

/**
 * Detect the font format from URL
 */
function getFontFormat(url: string): string {
  if (url.includes(".woff2")) return "woff2";
  if (url.includes(".woff")) return "woff";
  if (url.includes(".ttf")) return "truetype";
  if (url.includes(".otf")) return "opentype";
  return "woff2"; // Default assumption for Cloudinary
}

/**
 * Check if URL is a Google Fonts URL
 */
function isGoogleFontUrl(url: string): boolean {
  return url.includes("fonts.googleapis.com") || url.includes("fonts.gstatic.com");
}

/**
 * Apply theme overrides to a Shadow DOM.
 *
 * Splits the two kinds of override:
 * - CSS-variable overrides (colors + fonts) -> applyThemeVariablesToShadowRoot
 * - Raw custom CSS block -> applyCustomCssToShadowRoot (injected LAST so it wins)
 */
export function applyOverridesToShadowRoot(
  shadowRoot: ShadowRoot,
  overrides: WebComponentThemeOverrides
): void {
  const { customCss, ...themeVariables } = overrides;

  // Apply CSS-variable overrides first, then the raw custom CSS so that the
  // custom block is the last <style> in the shadow root and wins source-order ties.
  applyThemeVariablesToShadowRoot(shadowRoot, themeVariables);
  applyCustomCssToShadowRoot(shadowRoot, customCss);
}

/**
 * Inject a raw custom CSS block into the shadow root.
 *
 * Appended as the LAST <style> in the shadow root so that, at equal specificity,
 * source order makes it win over the component's base styles and the theme
 * variable overrides. This is the dashboard-set "Custom CSS" escape hatch.
 *
 * Safe against markup injection: textContent is not HTML-parsed, so a stray
 * "</style>" in the string cannot break out of the element.
 */
function applyCustomCssToShadowRoot(
  shadowRoot: ShadowRoot,
  customCss?: string
): void {
  const existing = shadowRoot.querySelector(
    "style[data-custom-css]"
  ) as HTMLStyleElement | null;

  const css = (customCss || "").trim();

  // No custom CSS configured - remove any stale block from a previous value
  if (!css) {
    if (existing) existing.remove();
    return;
  }

  const styleEl = existing || document.createElement("style");
  if (!existing) styleEl.setAttribute("data-custom-css", "true");
  styleEl.textContent = css;

  // Re-append to guarantee it is the last <style> so it always wins source-order ties
  shadowRoot.appendChild(styleEl);
}

/**
 * Apply theme overrides as CSS variables to Shadow DOM
 *
 * Only sets variables that have overrides - defaults come from globals.css
 * Sets both --{name} and --color-{name} for Tailwind v4 compatibility
 * Also handles custom font loading (Google Fonts via link, custom via @font-face)
 */
function applyThemeVariablesToShadowRoot(
  shadowRoot: ShadowRoot,
  overrides: WebComponentThemeOverrides
): void {
  // If no overrides, nothing to do - CSS defaults apply
  const entries = Object.entries(overrides).filter(([_, v]) => v != null && v !== "");
  if (entries.length === 0) return;

  // Find the existing style element or create one
  let styleEl = shadowRoot.querySelector('style[data-theme-overrides]') as HTMLStyleElement | null;

  if (!styleEl) {
    styleEl = document.createElement("style");
    styleEl.setAttribute("data-theme-overrides", "true");
    shadowRoot.appendChild(styleEl);
  }

  // Separate color overrides from font overrides
  const colorEntries = entries.filter(([key]) =>
    !key.startsWith("font")
  );

  // Generate CSS variables string for color overrides
  const colorVars = colorEntries
    .map(([key, value]) => {
      const cssVarName = toKebabCase(key);
      return `  --${cssVarName}: ${value};\n  --color-${cssVarName}: ${value};`;
    })
    .join("\n");

  // Handle font overrides
  let fontFaceCss = "";
  let fontVars = "";

  for (const [fontType, config] of Object.entries(FONT_CONFIG)) {
    const fontUrl = overrides[config.urlKey as keyof WebComponentThemeOverrides];
    const fontName = overrides[config.nameKey as keyof WebComponentThemeOverrides];

    if (!fontUrl || !fontName) continue;

    if (isGoogleFontUrl(fontUrl)) {
      // Google Fonts - inject link element into shadow root
      // Check if link already exists
      const existingLink = shadowRoot.querySelector(`link[data-font-type="${fontType}"]`);
      if (!existingLink) {
        const link = document.createElement("link");
        link.href = fontUrl;
        link.rel = "stylesheet";
        link.setAttribute("data-font-type", fontType);
        shadowRoot.appendChild(link);
      }

      // Set CSS variable for the font
      fontVars += `  ${config.cssVar}: "${fontName}", ${config.fallback};\n`;
    } else {
      // Custom font upload (Cloudinary) - generate @font-face
      const format = getFontFormat(fontUrl);
      fontFaceCss += `
@font-face {
  font-family: "${fontName}";
  src: url("${fontUrl}") format("${format}");
  font-weight: 100 900;
  font-style: normal;
  font-display: swap;
}
`;
      // Set CSS variable for the font
      fontVars += `  ${config.cssVar}: "${fontName}", ${config.fallback};\n`;
    }
  }

  // Combine all CSS
  let css = "";
  if (fontFaceCss) {
    css += fontFaceCss;
  }
  if (colorVars || fontVars) {
    css += `:host {\n${colorVars}${fontVars}}`;
  }

  // If a custom sans-serif font is configured, also override the global font-family
  // This is needed because globals.css has hardcoded font-family on all elements
  const sansUrl = overrides[FONT_CONFIG.sans.urlKey as keyof WebComponentThemeOverrides];
  const sansName = overrides[FONT_CONFIG.sans.nameKey as keyof WebComponentThemeOverrides];
  if (sansUrl && sansName) {
    const sansFallback = FONT_CONFIG.sans.fallback;
    css += `\n* { font-family: "${sansName}", ${sansFallback} !important; }`;
  }

  styleEl.textContent = css;
}

/**
 * Fetch and apply theme overrides to a shadow root.
 *
 * Call this when the web component mounts to load any school-specific
 * theme customizations on top of the CSS defaults.
 *
 * Stale-while-revalidate: any cached theme is applied IMMEDIATELY
 * (synchronously, before the browser paints) so repeat visitors never see
 * the default palette flash before their school's brand. Revalidation then
 * happens in the background and only re-applies when the school actually
 * changed their theme.
 *
 * @param businessIdentifier - Either businessSlug or businessId (ObjectId)
 */
export async function loadAndApplyThemeOverrides(
  businessIdentifier: string,
  shadowRoot: ShadowRoot,
  apiUrl?: string
): Promise<WebComponentThemeOverrides> {
  const cached = getCachedOverrides(businessIdentifier);

  if (cached) {
    // Apply the known brand before first paint — never hold it hostage
    // behind a network round-trip.
    applyOverridesToShadowRoot(shadowRoot, cached.overrides);

    const cacheAge = Date.now() - cached.timestamp;
    if (cacheAge < VERSION_CHECK_INTERVAL_MS) {
      return cached.overrides;
    }

    // Stale - revalidate in the background
    try {
      const currentVersion = await fetchVersionFromAPI(businessIdentifier, apiUrl);

      // Version matches - cache is still valid, refresh its timestamp
      if (currentVersion === cached.version) {
        cacheOverrides(businessIdentifier, cached.overrides, cached.version);
        return cached.overrides;
      }

      // Version changed - fetch and apply the new theme (the one case where
      // a visible shift is correct: the school actually rebranded)
      const { overrides, version } = await fetchOverridesFromAPI(businessIdentifier, apiUrl);
      cacheOverrides(businessIdentifier, overrides, version);
      applyOverridesToShadowRoot(shadowRoot, overrides);
      return overrides;
    } catch (error) {
      // On error, keep the already-applied cached overrides
      console.warn("[Baluu] Failed to revalidate theme, using cached:", error);
      return cached.overrides;
    }
  }

  // First visit - no cache; fetch and apply when it arrives
  try {
    const { overrides, version } = await fetchOverridesFromAPI(businessIdentifier, apiUrl);
    cacheOverrides(businessIdentifier, overrides, version);
    applyOverridesToShadowRoot(shadowRoot, overrides);
    return overrides;
  } catch (error) {
    console.warn("[Baluu] Failed to fetch theme overrides:", error);
    return {}; // No overrides, CSS defaults stay in effect
  }
}

